/*
 * Copyright 2023 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __assign } from "tslib";
import classNames from "classnames";
import { Classes as CoreClasses } from "@blueprintjs/core";
import { Classes as DatetimeClasses } from "@blueprintjs/datetime";
var RDP = "rdp";
var RDP_DAY = "".concat(RDP, "-day");
/** Class names applied by react-day-picker v8.x */
export var ReactDayPickerClasses = {
    RDP: RDP,
    RDP_CAPTION: "".concat(RDP, "-caption"),
    RDP_CAPTION_DROPDOWNS: "".concat(RDP, "-caption_dropdowns"),
    RDP_CAPTION_LABEL: "".concat(RDP, "-caption_label"),
    RDP_DAY: RDP_DAY,
    RDP_DAY_DISABLED: "".concat(RDP_DAY, "_disabled"),
    RDP_DAY_HOVERED_RANGE: "".concat(RDP_DAY, "_hovered"),
    RDP_DAY_HOVERED_RANGE_END: "".concat(RDP_DAY, "_hovered_end"),
    RDP_DAY_HOVERED_RANGE_START: "".concat(RDP_DAY, "_hovered_start"),
    RDP_DAY_OUTSIDE: "".concat(RDP_DAY, "_outside"),
    RDP_DAY_RANGE_END: "".concat(RDP_DAY, "_range_end"),
    RDP_DAY_RANGE_MIDDLE: "".concat(RDP_DAY, "_range_middle"),
    RDP_DAY_RANGE_START: "".concat(RDP_DAY, "_range_start"),
    RDP_DAY_SELECTED: "".concat(RDP_DAY, "_selected"),
    RDP_DAY_TODAY: "".concat(RDP_DAY, "_today"),
    RDP_MONTH: "".concat(RDP, "-month"),
    RDP_NAV: "".concat(RDP, "-nav"),
    RDP_TABLE: "".concat(RDP, "-table"),
    RDP_VHIDDEN: "".concat(RDP, "-vhidden"),
};
export var DatePicker3CaptionClasses = {
    DATEPICKER3_CAPTION: DatetimeClasses.DATEPICKER_CAPTION,
    DATEPICKER3_DROPDOWN_MONTH: DatetimeClasses.DATEPICKER_MONTH_SELECT,
    DATEPICKER3_DROPDOWN_YEAR: DatetimeClasses.DATEPICKER_YEAR_SELECT,
    DATEPICKER3_NAV_BUTTON: "".concat(DatetimeClasses.DATEPICKER, "-nav-button"),
    DATEPICKER3_NAV_BUTTON_NEXT: "".concat(DatetimeClasses.DATEPICKER, "-nav-button-next"),
    DATEPICKER3_NAV_BUTTON_PREVIOUS: "".concat(DatetimeClasses.DATEPICKER, "-nav-button-previous"),
};
export var DatePicker3Classes = {
    DATEPICKER3_DAY: RDP_DAY,
    DATEPICKER3_DAY_DISABLED: ReactDayPickerClasses.RDP_DAY_DISABLED,
    DATEPICKER3_DAY_IS_TODAY: ReactDayPickerClasses.RDP_DAY_TODAY,
    DATEPICKER3_DAY_OUTSIDE: ReactDayPickerClasses.RDP_DAY_OUTSIDE,
    DATEPICKER3_DAY_SELECTED: ReactDayPickerClasses.RDP_DAY_SELECTED,
    DATEPICKER3_HIGHLIGHT_CURRENT_DAY: "".concat(DatetimeClasses.DATEPICKER, "-highlight-current-day"),
    DATEPICKER3_REVERSE_MONTH_AND_YEAR: "".concat(DatetimeClasses.DATEPICKER, "-reverse-month-and-year"),
};
export var DateRangePicker3Classes = {
    DATERANGEPICKER3_HOVERED_RANGE: ReactDayPickerClasses.RDP_DAY_HOVERED_RANGE,
    DATERANGEPICKER3_HOVERED_RANGE_END: ReactDayPickerClasses.RDP_DAY_HOVERED_RANGE_END,
    DATERANGEPICKER3_HOVERED_RANGE_START: ReactDayPickerClasses.RDP_DAY_HOVERED_RANGE_START,
    DATERANGEPICKER3_REVERSE_MONTH_AND_YEAR: "".concat(DatetimeClasses.DATERANGEPICKER, "-reverse-month-and-year"),
    DATERANGEPICKER3_SELECTED_RANGE_END: ReactDayPickerClasses.RDP_DAY_RANGE_END,
    DATERANGEPICKER3_SELECTED_RANGE_MIDDLE: ReactDayPickerClasses.RDP_DAY_RANGE_MIDDLE,
    DATERANGEPICKER3_SELECTED_RANGE_START: ReactDayPickerClasses.RDP_DAY_RANGE_START,
    DATERANGEPICKER3_TIMEPICKERS_STACKED: "".concat(DatetimeClasses.DATERANGEPICKER_TIMEPICKERS, "-stacked"),
};
/** Class names for next-gen @blueprintjs/datetime2 "V3" components */
export var Classes = __assign(__assign(__assign(__assign({}, DatetimeClasses), DatePicker3CaptionClasses), DatePicker3Classes), DateRangePicker3Classes);
/**
 * Class name overrides for components rendered by react-day-picker. These are helpful so that @blueprintjs/datetime2
 * can have more predictable and standard DOM selectors in custom styles & tests.
 */
export var dayPickerClassNameOverrides = {
    /* eslint-disable camelcase */
    button: classNames(CoreClasses.BUTTON, CoreClasses.MINIMAL),
    // no need for button "reset" styles since the core Button styles handle that for us
    button_reset: undefined,
    dropdown_month: DatePicker3CaptionClasses.DATEPICKER3_DROPDOWN_MONTH,
    dropdown_year: DatePicker3CaptionClasses.DATEPICKER3_DROPDOWN_YEAR,
    nav_button: DatePicker3CaptionClasses.DATEPICKER3_NAV_BUTTON,
    nav_button_next: DatePicker3CaptionClasses.DATEPICKER3_NAV_BUTTON_NEXT,
    nav_button_previous: DatePicker3CaptionClasses.DATEPICKER3_NAV_BUTTON_PREVIOUS,
    /* eslint-enable camelcase */
};
//# sourceMappingURL=classes.js.map